import { useState, useRef } from "react";
import { Phone, Mail, MapPin, Upload, X, Send, ShieldCheck, ArrowUpRightSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { CONTACT as TIDS } from "@/constants/testIds";
import { COMPANY, PROJECT_TYPES } from "@/config/site";
import Placeholder from "@/components/Placeholder";
import useSEO, { SITE_URL } from "@/hooks/useSEO";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_FILE_MB = 5;

function fileToDataURL(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function ContactPage() {
  useSEO({
    title: "Contact — Devis gratuit rénovation & climatisation à Fondettes",
    description:
      "Contactez PENOW à Fondettes (37230) — 06 88 21 39 71. Devis gratuit sous 48h pour vos projets de rénovation, climatisation réversible, pompe à chaleur, ravalement de façade, nettoyage de toiture ou isolation en Indre-et-Loire.",
    keywords:
      "devis rénovation Fondettes, devis climatisation gratuit 37, contact artisan Tours, entreprise rénovation Indre-et-Loire contact",
    path: "/contact",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      url: `${SITE_URL}/contact`,
      name: "Contact PENOW",
      description: "Formulaire de contact et coordonnées PENOW à Fondettes (37230)",
    },
  });

  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    project_type: PROJECT_TYPES[0],
    message: "",
  });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_FILE_MB * 1024 * 1024) {
      toast.error(`Fichier trop volumineux (max ${MAX_FILE_MB} Mo).`);
      return;
    }
    setFile(f);
  };

  const removeFile = () => {
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);

    try {
      let attachment_data_url = null;
      if (file) attachment_data_url = await fileToDataURL(file);

      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        project_type: form.project_type,
        message: form.message.trim(),
        attachment_data_url,
        attachment_name: file?.name || null,
      });

      toast.success("Demande envoyée ! Nous vous recontactons sous 48h.");
      setSuccess(true);
      setForm({
        name: "",
        phone: "",
        email: "",
        project_type: PROJECT_TYPES[0],
        message: "",
      });
      removeFile();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Erreur d'envoi. Veuillez réessayer ou nous appeler.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid={TIDS.section}>
      <section className="container-editorial pt-16 md:pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left — info */}
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">Contact</div>
            <h1 className="font-display text-5xl md:text-6xl leading-tight text-anthracite">
              Parlons de
              <br />
              <span className="text-terracotta">votre projet.</span>
            </h1>
            <p className="mt-6 text-anthracite/70 leading-relaxed max-w-md">
              Décrivez-nous votre besoin en quelques mots. Nous transmettons votre
              demande à l'un de nos professionnels partenaires spécialisés, qui
              revient vers vous sous 48h avec une première réponse et, si
              nécessaire, un devis gratuit.
            </p>
            <p className="mt-3 text-xs text-anthracite/50 italic max-w-md">
              PENOW est une plateforme de mise en relation. La demande sera
              traitée directement par un professionnel partenaire, pas par nous.
            </p>

            <div className="mt-10 space-y-3">
              <a
                href={COMPANY.phoneHref}
                data-testid={TIDS.callBtn}
                className="flex items-center gap-4 bg-terracotta hover:bg-copper text-white p-5 transition-colors"
              >
                <div className="w-11 h-11 border border-white/40 flex items-center justify-center">
                  <Phone className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-white/70">Appelez-nous</div>
                  <div className="text-lg font-medium">{COMPANY.phone}</div>
                </div>
              </a>

              <div className="flex items-start gap-4 bg-white border border-black/10 p-5">
                <div className="w-11 h-11 bg-terracotta/10 text-terracotta flex items-center justify-center">
                  <Mail className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-anthracite/50">Email</div>
                  <div className="text-anthracite">{COMPANY.email}</div>
                </div>
              </div>

              <div className="flex items-start gap-4 bg-white border border-black/10 p-5">
                <div className="w-11 h-11 bg-terracotta/10 text-terracotta flex items-center justify-center">
                  <MapPin className="w-5 h-5" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.28em] text-anthracite/50">Adresse</div>
                  <div className="text-anthracite">{COMPANY.address}</div>
                  <div className="text-sm text-anthracite/60 mt-1">Zone : {COMPANY.zone}</div>
                </div>
              </div>
            </div>

            <div className="mt-8 flex items-center gap-3 text-xs text-anthracite/60">
              <ShieldCheck className="w-4 h-4 text-terracotta" strokeWidth={1.5} />
              Vos informations restent confidentielles et ne sont jamais transmises.
            </div>
          </div>

          {/* Right — Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              data-testid={TIDS.form}
              className="bg-white border border-black/10 p-6 md:p-10"
            >
              {success && (
                <div
                  data-testid={TIDS.success}
                  className="mb-8 border border-terracotta/40 bg-terracotta/10 text-anthracite p-4"
                >
                  <div className="font-medium">Message bien reçu !</div>
                  <div className="text-sm text-anthracite/70">
                    Nous revenons vers vous très vite. Un e-mail de confirmation
                    vous a été envoyé.
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FormField label="Nom complet" required>
                  <input
                    data-testid={TIDS.name}
                    required
                    type="text"
                    value={form.name}
                    onChange={update("name")}
                    className="input"
                    placeholder="Prénom Nom"
                  />
                </FormField>
                <FormField label="Téléphone" required>
                  <input
                    data-testid={TIDS.phone}
                    required
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="input"
                    placeholder="06 00 00 00 00"
                  />
                </FormField>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                <FormField label="Email" required>
                  <input
                    data-testid={TIDS.email}
                    required
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="input"
                    placeholder="votre@email.fr"
                  />
                </FormField>
                <FormField label="Type de travaux" required>
                  <select
                    data-testid={TIDS.projectType}
                    required
                    value={form.project_type}
                    onChange={update("project_type")}
                    className="input"
                  >
                    {PROJECT_TYPES.map((p) => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </FormField>
              </div>

              <div className="mt-5">
                <FormField label="Décrivez votre projet" required>
                  <textarea
                    data-testid={TIDS.message}
                    required
                    minLength={5}
                    value={form.message}
                    onChange={update("message")}
                    rows={6}
                    className="input resize-none"
                    placeholder="Surface, contexte, délais souhaités..."
                  />
                </FormField>
              </div>

              {/* File upload */}
              <div className="mt-5">
                <label className="block text-[11px] uppercase tracking-[0.22em] text-anthracite/60 mb-2">
                  Photo du chantier (optionnel)
                </label>
                {!file ? (
                  <label className="flex items-center justify-center gap-3 border border-dashed border-black/20 hover:border-terracotta hover:bg-terracotta/5 transition-colors p-6 cursor-pointer">
                    <Upload className="w-5 h-5 text-anthracite/60" strokeWidth={1.5} />
                    <span className="text-sm text-anthracite/70">
                      Cliquez pour ajouter une photo (max {MAX_FILE_MB} Mo)
                    </span>
                    <input
                      ref={fileInputRef}
                      data-testid={TIDS.file}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={onFile}
                    />
                  </label>
                ) : (
                  <div className="flex items-center gap-3 border border-black/10 bg-sand p-3">
                    <div className="w-16">
                      <Placeholder icon="camera" label="" ratio="1/1" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm text-anthracite truncate">{file.name}</div>
                      <div className="text-xs text-anthracite/50">
                        {(file.size / 1024).toFixed(0)} ko
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={removeFile}
                      className="p-2 hover:bg-black/5"
                      aria-label="Retirer le fichier"
                    >
                      <X className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  </div>
                )}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-4">
                <button
                  type="submit"
                  data-testid={TIDS.submit}
                  disabled={submitting}
                  className="inline-flex items-center gap-2 bg-terracotta hover:bg-copper disabled:opacity-60 disabled:cursor-not-allowed text-white px-7 py-4 tracking-wide transition-colors"
                >
                  {submitting ? "Envoi en cours..." : "Envoyer ma demande"}
                  <Send className="w-4 h-4" strokeWidth={1.5} />
                </button>
                <div className="text-xs text-anthracite/50">
                  Réponse sous 48h · Devis gratuit
                </div>
              </div>

              {/* RGPD notice — mandatory per Art. 13 GDPR */}
              <p className="mt-6 text-xs text-anthracite/50 leading-relaxed border-t border-black/5 pt-4">
                Les informations recueillies via ce formulaire sont enregistrées
                par PENOW puis transmises à un professionnel partenaire pour
                répondre à votre demande. Vous disposez d'un droit d'accès, de
                rectification, d'effacement et d'opposition sur vos données. Pour
                en savoir plus, consultez notre{" "}
                <a href="/confidentialite" className="underline hover:text-terracotta">
                  politique de confidentialité
                </a>.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* Map — OpenStreetMap embed of Fondettes 37230 */}
      <section className="container-editorial pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4">
            <div className="eyebrow mb-4">Notre zone d'intervention</div>
            <h2 className="font-display text-3xl text-anthracite">
              {COMPANY.zone}
            </h2>
            <p className="mt-4 text-anthracite/60 leading-relaxed">
              Basés à Fondettes (37230), nous intervenons dans toute l'Indre-et-Loire
              et étendons nos prestations à l'ensemble de la région Centre-Val de Loire
              pour les chantiers importants.
            </p>
            <a
              href="https://www.google.com/maps/search/?api=1&query=Fondettes+37230"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 link-arrow text-terracotta text-sm"
              data-testid="contact-open-maps"
            >
              Ouvrir dans Google Maps
              <ArrowUpRightSquare className="w-4 h-4" strokeWidth={1.5} />
            </a>
          </div>
          <div className="lg:col-span-8">
            <div className="border border-black/10 overflow-hidden" style={{ aspectRatio: "16/9" }}>
              <iframe
                title="Carte — Fondettes 37230"
                src="https://www.openstreetmap.org/export/embed.html?bbox=0.55%2C47.36%2C0.66%2C47.42&layer=mapnik&marker=47.389%2C0.601"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Local styles for inputs */}
      <style>{`
        .input {
          width: 100%;
          background: #F9F9F8;
          border: 1px solid rgba(0,0,0,0.1);
          padding: 12px 14px;
          font-family: Manrope, sans-serif;
          font-size: 14px;
          color: #1A1C20;
          outline: none;
          transition: border-color 200ms ease, background 200ms ease;
        }
        .input:focus {
          border-color: #C05C3D;
          background: #ffffff;
        }
      `}</style>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-anthracite/60 mb-2">
        {label} {required && <span className="text-terracotta">*</span>}
      </span>
      {children}
    </label>
  );
}
